import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Play, X } from "@phosphor-icons/react";
import { Reveal } from "@/components/Reveal";

// To use real footage instead of the animated film: set this to your video URL
// (e.g. "/assets/launch-film.mp4" after uploading the file, or a hosted mp4 URL).
const LAUNCH_FILM_VIDEO_URL = null;

const SCENES = [
    { img: "/assets/img/transformation.jpg", kicker: "Every business", line: "A hundred opportunities a week.\nNo system to catch them.", dur: 7000 },
    { img: "/assets/img/hero.jpg", kicker: "", line: "What if your business could respond\nto every opportunity?", dur: 7000 },
    { img: "/assets/img/devices.jpg", kicker: "Build", line: "A premium website that sells\nwhile you sleep.", dur: 7000 },
    { img: "/assets/img/ai-employee.jpg", kicker: "Automate", line: "AI employees that answer, qualify\nand book — around the clock.", dur: 7500 },
    { img: "/assets/img/crm.jpg", kicker: "Connect", line: "Every enquiry. Every booking. Every follow-up.\nOne connected system.", dur: 7500 },
    { img: "/assets/img/property-1.jpg", kicker: "Any industry", line: "Estate agencies. Golf clubs. Fitness studios.\nLocal businesses. Global ambitions.", dur: 7000 },
    { img: "/assets/img/ecosystem.jpg", kicker: "", line: "Not another website.\nNot another marketing package.", dur: 6500 },
    { img: "/assets/img/ecosystem.jpg", kicker: "A connected growth system", line: "Build it. Automate it.\nOptimise it. Grow it.", dur: 7000 },
];

const TOTAL = SCENES.reduce((s, sc) => s + sc.dur, 0);

function EndCard({ onClose }) {
    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1 }}
            className="absolute inset-0 z-20 flex flex-col items-center justify-center bg-black px-6 text-center"
            data-testid="film-endcard"
        >
            <motion.p
                initial={{ opacity: 0, letterSpacing: "0.8em" }}
                animate={{ opacity: 1, letterSpacing: "0.35em" }}
                transition={{ duration: 1.6, delay: 0.3 }}
                className="font-display text-4xl font-black text-white sm:text-6xl"
            >
                HIGHTS
            </motion.p>
            <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.2, duration: 1 }}
                className="mt-5 text-xs font-light uppercase tracking-[0.4em] text-white/55 sm:text-sm"
            >
                Your business. Engineered to grow.
            </motion.p>
            <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 2, duration: 0.8 }}
                className="mt-12 flex flex-col gap-3 sm:flex-row"
            >
                <Link
                    to="/book"
                    onClick={onClose}
                    data-testid="film-book-button"
                    className="bg-white px-9 py-4 text-xs font-bold uppercase tracking-[0.25em] text-black transition-colors hover:bg-white/85"
                >
                    Book your free consultation
                </Link>
                <button
                    onClick={onClose}
                    data-testid="film-close-endcard"
                    className="border border-white/25 px-9 py-4 text-xs font-bold uppercase tracking-[0.25em] text-white transition-colors hover:bg-white/10"
                >
                    Back to the experience
                </button>
            </motion.div>
        </motion.div>
    );
}

function FilmPlayer({ onClose }) {
    const [scene, setScene] = useState(0);
    const [ended, setEnded] = useState(false);
    const [elapsed, setElapsed] = useState(0);
    const timer = useRef(null);
    const startRef = useRef(Date.now());

    useEffect(() => {
        startRef.current = Date.now();
        const tick = setInterval(() => setElapsed(Date.now() - startRef.current), 100);
        return () => clearInterval(tick);
    }, []);

    useEffect(() => {
        if (ended) return;
        timer.current = setTimeout(() => {
            if (scene < SCENES.length - 1) setScene(scene + 1);
            else setEnded(true);
        }, SCENES[scene].dur);
        return () => clearTimeout(timer.current);
    }, [scene, ended]);

    const current = SCENES[scene];

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[120] bg-black"
            data-testid="film-player"
            role="dialog"
            aria-label="HIGHTS launch film"
        >
            {!ended && (
                <>
                    <AnimatePresence mode="sync">
                        <motion.img
                            key={scene}
                            src={current.img}
                            alt=""
                            initial={{ opacity: 0, scale: 1.02 }}
                            animate={{ opacity: 1, scale: 1.1 }}
                            exit={{ opacity: 0 }}
                            transition={{ opacity: { duration: 1.2 }, scale: { duration: current.dur / 1000 + 1.2, ease: "linear" } }}
                            className="absolute inset-0 h-full w-full object-cover"
                        />
                    </AnimatePresence>
                    <div className="absolute inset-0 bg-black/45" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-black/40" />

                    <div className="absolute inset-x-0 top-0 z-10 h-0.5 bg-white/15">
                        <motion.div
                            className="h-full bg-white"
                            style={{ width: `${Math.min((elapsed / TOTAL) * 100, 100)}%` }}
                            data-testid="film-progress"
                        />
                    </div>

                    <div className="absolute inset-x-0 bottom-0 z-10 flex flex-col items-center pb-16 text-center sm:pb-20">
                        <AnimatePresence mode="wait">
                            <motion.div
                                key={scene}
                                initial={{ opacity: 0, y: 24 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -14 }}
                                transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                                className="px-6"
                            >
                                {current.kicker && (
                                    <p className="mb-4 font-mono text-[10px] uppercase tracking-[0.45em] text-white/60 sm:text-xs">{current.kicker}</p>
                                )}
                                <p className="whitespace-pre-line font-display text-2xl font-black leading-tight tracking-tight text-white sm:text-4xl lg:text-5xl">
                                    {current.line}
                                </p>
                            </motion.div>
                        </AnimatePresence>
                        <div className="mt-10 flex items-center gap-2">
                            {SCENES.map((_, i) => (
                                <span key={i} className={`h-1 transition-all duration-500 ${i === scene ? "w-7 bg-white" : i < scene ? "w-2.5 bg-white/60" : "w-2.5 bg-white/20"}`} />
                            ))}
                        </div>
                    </div>

                    <button
                        onClick={() => setEnded(true)}
                        data-testid="film-skip-button"
                        className="absolute right-5 top-5 z-30 border border-white/25 bg-black/50 px-5 py-2.5 text-[10px] font-bold uppercase tracking-[0.25em] text-white/80 backdrop-blur-sm transition-colors hover:bg-white hover:text-black"
                    >
                        Skip to end
                    </button>
                </>
            )}
            {ended && <EndCard onClose={onClose} />}
            <button
                onClick={onClose}
                data-testid="film-close-button"
                aria-label="Close film"
                className="absolute left-5 top-5 z-30 flex h-11 w-11 items-center justify-center border border-white/25 bg-black/50 text-white backdrop-blur-sm transition-colors hover:bg-white hover:text-black"
            >
                <X size={18} />
            </button>
        </motion.div>
    );
}

export default function LaunchFilm() {
    const [playing, setPlaying] = useState(false);

    return (
        <section className="relative border-t border-white/10 py-28 md:py-36" data-testid="film-section">
            <div className="mx-auto max-w-[1400px] px-5 md:px-10">
                <Reveal>
                    <p className="font-mono text-[10px] uppercase tracking-[0.4em] text-white/40">The film</p>
                    <h2 className="mt-6 max-w-3xl font-display text-4xl font-black leading-[0.98] tracking-tighter text-white sm:text-5xl">
                        Sixty seconds inside a connected business.
                    </h2>
                </Reveal>
                <Reveal delay={0.15}>
                    <button
                        onClick={() => setPlaying(true)}
                        data-testid="film-play-button"
                        className="group relative mt-12 block w-full overflow-hidden border border-white/12 text-left"
                        aria-label="Play the HIGHTS launch film"
                    >
                        <div className="relative aspect-[16/9] w-full overflow-hidden sm:aspect-[21/9]">
                            <img
                                src="/assets/img/hero.jpg"
                                alt="Still from the HIGHTS launch film — a business running on one connected system"
                                className="h-full w-full object-cover opacity-70 transition-transform duration-[1.4s] ease-out group-hover:scale-[1.04]"
                                loading="lazy"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-black/40" />
                            <div className="absolute inset-0 flex items-center justify-center">
                                <span className="flex h-20 w-20 items-center justify-center rounded-full border border-white/40 bg-black/50 backdrop-blur-md transition-all duration-500 group-hover:scale-110 group-hover:bg-white group-hover:text-black sm:h-24 sm:w-24">
                                    <Play size={30} weight="fill" className="ml-1 text-white transition-colors duration-500 group-hover:text-black" />
                                </span>
                            </div>
                            <div className="absolute bottom-5 left-5 flex items-center gap-4">
                                <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/70">HIGHTS — Launch film</span>
                                <span className="border border-white/25 px-2 py-0.5 font-mono text-[9px] tracking-widest text-white/60">~60s</span>
                            </div>
                        </div>
                    </button>
                    <p className="mt-4 text-xs font-light text-white/35">
                        Currently playing: the cinematic motion cut. The moment your filmed advert is ready, it drops straight into this player.
                    </p>
                </Reveal>
            </div>
            <AnimatePresence>
                {playing && (
                    LAUNCH_FILM_VIDEO_URL ? (
                        <motion.div
                            key="video"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="fixed inset-0 z-[120] flex items-center justify-center bg-black"
                            data-testid="film-player"
                        >
                            <video src={LAUNCH_FILM_VIDEO_URL} controls autoPlay playsInline className="h-full w-full object-contain" />
                            <button
                                onClick={() => setPlaying(false)}
                                data-testid="film-close-button"
                                aria-label="Close film"
                                className="absolute left-5 top-5 z-30 flex h-11 w-11 items-center justify-center border border-white/25 bg-black/50 text-white backdrop-blur-sm transition-colors hover:bg-white hover:text-black"
                            >
                                <X size={18} />
                            </button>
                        </motion.div>
                    ) : (
                        <FilmPlayer key="animated" onClose={() => setPlaying(false)} />
                    )
                )}
            </AnimatePresence>
        </section>
    );
}
